import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box, Container, Stack, Grid,
  Divider, Card, CardContent, CardActions, Chip, IconButton,
  alpha, ThemeProvider, CssBaseline, Drawer, List, ListItemButton,
  ListItemText, Accordion, AccordionSummary, AccordionDetails, Paper,
} from '@mui/material';
import {
  Restaurant, CheckCircle, ArrowForward, Menu as MenuIcon, Close,
  Smartphone, Computer, Store, Wifi, Download, VpnKey, Security, Payment,
  ExpandMore, Lan, Key, Business, Search, TableRestaurant, ElectricMoped,
  LocalMall, Add,
} from '@mui/icons-material';
import { landingTheme, sectionSx } from '../theme/landingTheme';
import SectionHeader from './landing/SectionHeader';

const NAV = [
  { label: 'Features', ref: 'features' },
  { label: 'How it works', ref: 'how' },
  { label: 'Pricing', ref: 'pricing' },
  { label: 'FAQ', ref: 'faq' },
];

function ProductPreview() {
  const tables = [
    {
      name: 'MAIN HALL | T-04',
      occupied: true,
      bills: [
        { no: '#1042', amount: '₹1,240', status: 'Preparing', color: '#1976D2' },
        { no: '#1043', amount: '₹420', status: 'New', color: '#FF9800' },
      ],
    },
    {
      name: 'MAIN HALL | T-07',
      occupied: true,
      bills: [{ no: '#1038', amount: '₹680', status: 'Ready', color: '#2E7D32' }],
    },
    {
      name: 'TERRACE | T-02',
      occupied: false,
      bills: [],
    },
    {
      name: 'TERRACE | T-05',
      occupied: true,
      bills: [{ no: '#1045', amount: '₹2,180', status: 'Served', color: '#5C6BC0' }],
    },
  ];

  return (
    <Paper
      elevation={12}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: alpha('#fff', 0.25),
        boxShadow: '0 32px 64px -16px rgba(0,0,0,0.45)',
      }}
    >
      {/* Desktop window chrome */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: '#ececec',
          borderBottom: '1px solid #d4d4d4',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Stack direction="row" spacing={0.75}>
          {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
            <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
          ))}
        </Stack>
        <Typography sx={{ flex: 1, textAlign: 'center', fontSize: '0.72rem', fontWeight: 600, color: '#525252', mr: 4 }}>
          Home Billing POS — Floor dashboard
        </Typography>
        <Chip label="Offline" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700 }} />
      </Box>

      {/* POS app UI */}
      <Box sx={{ bgcolor: '#f4f4f5', p: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1.25,
              py: 0.5,
              borderRadius: 2,
              border: '0.5px solid #d9d9d9',
              bgcolor: '#fff',
              minWidth: { xs: 140, sm: 180 },
            }}
          >
            <Search sx={{ fontSize: 16, color: '#bdbdbd' }} />
            <Typography sx={{ ml: 0.75, fontSize: '0.7rem', color: '#9e9e9e' }}>Search by bill no</Typography>
          </Paper>
          {['Add table', 'Delivery', 'Pick up'].map((label) => (
            <Box
              key={label}
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 1.5,
                bgcolor: '#fff',
                fontSize: '0.65rem',
                fontWeight: 600,
                color: '#424242',
                boxShadow: '0 2px 8px rgba(149,157,165,0.25)',
                display: { xs: label === 'Add table' ? 'none' : 'block', sm: 'block' },
              }}
            >
              {label}
            </Box>
          ))}
        </Stack>

        <Paper elevation={0} sx={{ borderRadius: '8px 8px 0 0', mb: 0, overflow: 'hidden', boxShadow: '0 2px 8px rgba(149,157,165,0.12)' }}>
          <Stack direction="row">
            {[
              { label: 'Dine-In', icon: TableRestaurant, active: true },
              { label: 'Delivery', icon: ElectricMoped, active: false },
              { label: 'Pick Up', icon: LocalMall, active: false },
            ].map(({ label, icon: Icon, active }) => (
              <Box
                key={label}
                sx={{
                  flex: 1,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  bgcolor: active ? '#fff' : '#fafafa',
                  borderBottom: active ? '2px solid #4f46e5' : '2px solid transparent',
                  color: active ? '#4f46e5' : '#757575',
                }}
              >
                <Icon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700 }}>{label}</Typography>
              </Box>
            ))}
          </Stack>
        </Paper>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ py: 1, flexWrap: 'wrap' }}>
          {['All', 'Main Hall', 'Terrace'].map((area, i) => (
            <Box
              key={area}
              sx={{
                px: 1.25,
                py: 0.4,
                borderRadius: 1,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: i === 0 ? '#4f46e5' : '#fff',
                color: i === 0 ? '#fff' : '#616161',
                border: i === 0 ? 'none' : '1px solid #e0e0e0',
              }}
            >
              {area}
            </Box>
          ))}
          <Box sx={{ ml: 'auto', width: 22, height: 22, borderRadius: 1, bgcolor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Add sx={{ fontSize: 14, color: '#616161' }} />
          </Box>
        </Stack>

        <Grid container spacing={1.25}>
          {tables.map((table) => (
            <Grid key={table.name} size={{ xs: 6, sm: 6 }}>
              <Paper
                elevation={2}
                sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}
              >
                <Box
                  sx={{
                    py: 0.75,
                    px: 1,
                    bgcolor: table.occupied ? '#4f46e5' : '#9e9e9e',
                    color: '#fff',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                  }}
                >
                  {table.name}
                </Box>
                <Box sx={{ p: 0.75, minHeight: 52 }}>
                  {table.bills.length === 0 ? (
                    <Typography sx={{ fontSize: '0.62rem', color: '#757575', py: 1, textAlign: 'center' }}>
                      No active bills
                    </Typography>
                  ) : (
                    table.bills.map((bill) => (
                      <Stack
                        key={bill.no}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          py: 0.5,
                          px: 0.5,
                          mb: 0.25,
                          borderRadius: 1,
                          bgcolor: `${bill.color}14`,
                          borderLeft: `2px solid ${bill.color}`,
                        }}
                      >
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#424242' }}>{bill.no}</Typography>
                        <Typography sx={{ fontSize: '0.6rem', color: '#616161' }}>{bill.amount}</Typography>
                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: bill.color }}>{bill.status}</Typography>
                      </Stack>
                    ))
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );
}

function FeatureCard({ icon: Icon, title, description, badge }) {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.25s, box-shadow 0.25s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 40px -12px rgba(15,23,42,0.12)' },
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 3.5 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2.5 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: 2.5, background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon sx={{ color: '#fff', fontSize: 26 }} />
          </Box>
          {badge && <Chip label={badge} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, flexShrink: 0 }} />}
        </Stack>
        <Typography variant="h6" sx={{ mb: 1.25, fontWeight: 700 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{description}</Typography>
      </CardContent>
    </Card>
  );
}

function PricingCard({ plan, onCta }) {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: plan.highlight ? '2px solid' : '1px solid',
        borderColor: plan.highlight ? 'primary.main' : 'divider',
        position: 'relative',
        overflow: 'visible',
        boxShadow: plan.highlight ? '0 20px 48px -16px rgba(79, 70, 229, 0.35)' : undefined,
        pt: plan.highlight ? 2 : 0,
      }}
    >
      {plan.highlight && (
        <Chip
          label="Most popular"
          color="primary"
          size="small"
          sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 700, zIndex: 1 }}
        />
      )}
      <CardContent sx={{ p: { xs: 3, md: 4 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="overline" color="text.secondary">{plan.name}</Typography>
        <Typography variant="h3" sx={{ fontWeight: 800, my: 1 }}>{plan.price}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{plan.period}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: { md: 44 } }}>{plan.description}</Typography>
        <Stack spacing={1.5} sx={{ flex: 1 }}>
          {plan.features.map((f) => (
            <Stack key={f} direction="row" spacing={1.25} alignItems="flex-start">
              <CheckCircle sx={{ fontSize: 18, color: 'success.main', mt: 0.2, flexShrink: 0 }} />
              <Typography variant="body2">{f}</Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
      <CardActions sx={{ p: { xs: 3, md: 4 }, pt: 0, mt: 'auto' }}>
        <Button fullWidth variant={plan.highlight ? 'contained' : 'outlined'} size="large" onClick={onCta}>
          {plan.cta}
        </Button>
      </CardActions>
    </Card>
  );
}

export default function LandingPage({ onLoginClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const refs = {
    features: useRef(null),
    how: useRef(null),
    pricing: useRef(null),
    faq: useRef(null),
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (key) => {
    refs[key]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setDrawerOpen(false);
  };

  const features = [
    { icon: Download, title: '7 days free', description: 'Create an account and run your first desktop counter free for 7 days. No card required — pay only when you continue after trial.', badge: '7 days' },
    { icon: VpnKey, title: 'Cloud-linked counter PC', description: 'Link your billing counter to your cloud account. Paste the activation key once — LAN sync works even when internet is spotty.', },
    { icon: Store, title: 'Branches per location', description: 'One owner account, multiple shops. Each branch gets its own LAN sync key for devices at that site.', },
    { icon: Wifi, title: 'Mobile free on LAN', description: 'Waiter phones on the same branch Wi‑Fi sync with desktop at no extra license fee.', },
    { icon: Security, title: 'Offline-first', description: 'Built for counters where internet is unreliable. License verified locally on the device.', },
    { icon: Payment, title: 'Full billing flow', description: 'Tables, KOT, billing, day-end, and reports. Desktop runs the counter; mobile takes floor orders.', },
  ];

  const steps = [
    { n: '01', title: 'Create free account', desc: 'Sign up — your Main branch is created automatically with a sync key.' },
    { n: '02', title: 'Connect counter PC', desc: 'During your 7-day trial, connect your first counter PC to your cloud account. Copy the activation key from your dashboard.' },
    { n: '03', title: 'Sign in on counter PC', desc: 'Paste the key once. Your PC is bound to your cloud account and syncs mobile on your LAN.' },
    { n: '04', title: 'Connect mobile on LAN', desc: 'Waiter app joins the same branch network and syncs with desktop for free.' },
  ];

  const plans = [
    { name: '7-day trial', price: '₹0', period: 'Full access · no card', description: 'Keep everything free for 7 days — Main branch and one counter PC included.', features: ['7 days free on first branch', 'One counter PC per branch', 'Desktop & mobile app download', 'LAN mobile sync at branch', 'Then ₹1,999/year per branch'], cta: 'Start free trial', highlight: true },
    { name: 'Standard', price: '₹1,999', period: 'Per branch · per year', description: 'One shop location — one counter PC + mobile on LAN per branch.', features: ['₹1,999 every year per branch', 'One counter PC per branch', 'Extra branch: ₹1,999/year each', '1 branch on signup', 'Cloud activation key per branch'], cta: 'Create account', highlight: false },
    { name: 'Cloud', price: 'Custom', period: 'Multi-branch', description: 'For chains and franchises with many locations.', features: ['Unlimited branches', 'Sync key per branch', 'Central owner dashboard', 'Multi-location reporting', 'Standard plan per counter PC'], cta: 'Contact sales', highlight: false },
  ];

  const faqs = [
    { q: 'How does the 7-day free trial work?', a: 'Sign up and connect one counter PC to your Main branch at no cost. You get full POS access for 7 days. After that, every branch is ₹1,999/year. Each branch allows one counter PC registration, once only — revoking access does not free the slot.' },
    { q: 'Does the counter PC need internet every day?', a: 'No. After you paste the license key, the desktop validates offline. Internet is only needed when purchasing or renewing from your online dashboard.' },
    { q: 'Do I pay for mobile waiter phones?', a: 'No extra charge on your shop LAN. Mobile syncs with the connected counter PC at the same branch.' },
    { q: 'What is a branch?', a: 'A physical shop location under your restaurant account. Standard includes one branch; Cloud supports unlimited branches, each with its own sync key.' },
    { q: 'Where do I go after signup?', a: 'Sign in and open your dashboard to connect counter PCs, manage branches, and copy activation keys. Download the POS app from your dashboard when you are ready to go live.' },
  ];

  const navColor = scrolled ? 'text.primary' : '#fff';

  return (
    <ThemeProvider theme={landingTheme}>
      <CssBaseline />
      <Box sx={{ bgcolor: 'background.default' }}>
        {/* Navbar */}
        <AppBar position="fixed" elevation={0} sx={{ bgcolor: scrolled ? alpha('#fff', 0.92) : 'transparent', backdropFilter: scrolled ? 'blur(16px)' : 'none', borderBottom: scrolled ? '1px solid' : 'none', borderColor: 'divider', transition: 'all 0.3s' }}>
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 } }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                <Box 
                  sx={{ 
                    bgcolor: '#ffffff',
                    p: 0.75,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                  }}
                >
                  <img src="/logo192.png" alt="HomeBillingPOS Logo" style={{ height: 32, width: 32, objectFit: 'contain' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: navColor, lineHeight: 1.1 }}>Home Billing POS</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: scrolled ? 'text.secondary' : alpha('#fff', 0.75) }}>Offline restaurant POS</Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                {NAV.map((item) => (
                  <Button key={item.ref} onClick={() => scrollTo(item.ref)} sx={{ color: navColor, fontWeight: 600, px: 1.5 }}>{item.label}</Button>
                ))}
                <Button variant="contained" onClick={onLoginClick} sx={{ ml: 1, ...(scrolled ? {} : { bgcolor: '#fff', color: 'primary.main', '&:hover': { bgcolor: alpha('#fff', 0.9) } }) }}>
                  Sign in
                </Button>
              </Stack>
              <IconButton sx={{ display: { md: 'none' }, color: navColor }} onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>

        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 280, pt: 2 }}>
            <Stack direction="row" justifyContent="flex-end" sx={{ px: 1 }}>
              <IconButton onClick={() => setDrawerOpen(false)}><Close /></IconButton>
            </Stack>
            <List>
              {NAV.map((item) => (
                <ListItemButton key={item.ref} onClick={() => scrollTo(item.ref)}>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
              <ListItemButton onClick={() => { setDrawerOpen(false); onLoginClick(); }}>
                <ListItemText primary="Sign in" primaryTypographyProps={{ fontWeight: 700, color: 'primary.main' }} />
              </ListItemButton>
            </List>
          </Box>
        </Drawer>

        {/* Hero */}
        <Box sx={{ background: 'linear-gradient(145deg, #312e81 0%, #4f46e5 45%, #6366f1 100%)', color: '#fff', pt: { xs: 14, md: 18 }, pb: { xs: 10, md: 14 }, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', width: 480, height: 480, borderRadius: '50%', background: alpha('#fff', 0.06), top: -120, right: -80 }} />
          <Box sx={{ position: 'absolute', width: 320, height: 320, borderRadius: '50%', background: alpha('#fff', 0.04), bottom: -80, left: -60 }} />
          <Container maxWidth="lg" sx={{ position: 'relative' }}>
            <Grid container spacing={{ xs: 6, lg: 8 }} alignItems="center" sx={{ width: '100%' }}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Chip label="7 days free · Cloud standard · Counter PC sync" sx={{ mb: 3, bgcolor: alpha('#fff', 0.12), color: '#fff', fontWeight: 600, border: `1px solid ${alpha('#fff', 0.2)}` }} />
                <Typography variant="h1" sx={{ color: '#fff', mb: 3 }}>
                  POS that works when the internet doesn&apos;t
                </Typography>
                <Typography sx={{ color: alpha('#fff', 0.88), fontSize: '1.125rem', lineHeight: 1.75, mb: 4, maxWidth: 520 }}>
                  Keep it free for 7 days — full POS on your first desktop, no card needed.
                  After trial, ₹1,999 every year per branch. Mobile on your shop Wi‑Fi stays free.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
                  <Button variant="contained" size="large" onClick={onLoginClick} sx={{ bgcolor: '#fff', color: 'primary.main', px: 4, py: 1.5, '&:hover': { bgcolor: alpha('#fff', 0.92) } }} endIcon={<ArrowForward />}>
                    Create free account
                  </Button>
                  <Button variant="outlined" size="large" onClick={() => scrollTo('pricing')} sx={{ borderColor: alpha('#fff', 0.5), color: '#fff', px: 4, py: 1.5, '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.08) } }}>
                    View pricing
                  </Button>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} divider={<Divider orientation="vertical" flexItem sx={{ borderColor: alpha('#fff', 0.2), display: { xs: 'none', sm: 'block' } }} />}>
                  {[{ v: '7 days', l: 'Free trial' }, { v: 'Offline', l: 'After key paste' }, { v: '₹1,999', l: 'Per branch/year' }].map((s) => (
                    <Box key={s.l}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.35rem' }}>{s.v}</Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: alpha('#fff', 0.7) }}>{s.l}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <ProductPreview />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Trust strip */}
        <Box sx={{ py: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="center" alignItems="center" flexWrap="wrap">
              {['7-day free trial', 'Cloud account + activation key', 'Branch LAN sync', 'Mobile free on Wi‑Fi'].map((t) => (
                <Stack key={t} direction="row" spacing={1} alignItems="center">
                  <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                  <Typography variant="body2" fontWeight={600} color="text.secondary">{t}</Typography>
                </Stack>
              ))}
            </Stack>
          </Container>
        </Box>

        {/* Features */}
        <Box component="section" sx={{ ...sectionSx, width: '100%', overflow: 'hidden' }} ref={refs.features}>
          <Container maxWidth="lg">
            <SectionHeader
              overline="CAPABILITIES"
              title="Everything a real shop floor needs"
              subtitle="7-day free trial on your first branch. Then ₹1,999 every year per branch — mobile on your LAN stays free."
            />
            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ width: '100%', alignItems: 'stretch' }}>
              {features.map((f) => (
                <Grid key={f.title} size={{ xs: 12, sm: 6, lg: 4 }} sx={{ display: 'flex' }}>
                  <FeatureCard {...f} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* How it works */}
        <Box sx={{ bgcolor: alpha('#4f46e5', 0.04), ...sectionSx }} ref={refs.how}>
          <Container maxWidth="lg">
            <SectionHeader overline="GET STARTED" title="Four steps to a live counter" subtitle="From signup to offline billing — no complicated cloud setup." />
            <Grid container spacing={3} sx={{ width: '100%' }}>
              {steps.map((step) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={step.n}>
                  <Card sx={{ height: '100%', textAlign: 'left' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: '2rem', lineHeight: 1, mb: 2 }}>{step.n}</Typography>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>{step.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{step.desc}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Cloud setup */}
        <Container maxWidth="lg" sx={{ ...sectionSx }}>
          <Grid container spacing={6} alignItems="center" sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <SectionHeader overline="CLOUD SETUP" title="Counter PC on cloud · Mobile free on LAN" subtitle="Standard plan links your billing counter to your cloud account. Waiter phones on the same branch network sync at no extra cost." align="left" maxWidth="100%" />
              <Stack spacing={2.5}>
                {[
                  { icon: Computer, title: 'Counter PC — cloud linked', desc: 'Main billing counter. Connect once in your owner portal. Mobile syncs over your shop LAN.', color: '#4f46e5' },
                  { icon: Smartphone, title: 'Mobile — free on LAN', desc: 'Order-taking on shop Wi‑Fi. Syncs with desktop at the same branch.', color: '#0ea5e9' },
                  { icon: Business, title: 'Branches', desc: 'Standard: 1 location. Cloud: unlimited branches, each with its own sync key.', color: '#7c3aed' },
                ].map((d) => (
                  <Stack key={d.title} direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(d.color, 0.1), color: d.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <d.icon />
                    </Box>
                    <Box>
                      <Typography fontWeight={700} sx={{ mb: 0.5 }}>{d.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{d.desc}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Card sx={{ p: { xs: 3, md: 4 } }}>
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  {[
                    { icon: Key, label: 'Activation key', value: 'Paste once on counter PC' },
                    { icon: Lan, label: 'Branch sync key', value: 'Mobile + counter at one location' },
                    { icon: Store, label: 'Owner dashboard', value: 'Branches · counter PCs · billing' },
                  ].map((item) => (
                    <Grid size={{ xs: 12, sm: 4 }} key={item.label}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#4f46e5', 0.04), height: '100%' }}>
                        <item.icon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                        <Typography variant="subtitle2" fontWeight={700}>{item.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Pricing */}
        <Box
          component="section"
          sx={{
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            ...sectionSx,
            width: '100%',
            overflow: 'hidden',
          }}
          ref={refs.pricing}
        >
          <Container maxWidth="lg">
            <SectionHeader
              overline="PRICING"
              title="Simple, honest pricing"
              subtitle="Keep free for 7 days after signup. Then ₹1,999 every year per branch — mobile on your LAN stays free."
            />
            <Grid
              container
              spacing={{ xs: 2, md: 3 }}
              sx={{ width: '100%', alignItems: 'stretch', mb: 4 }}
            >
              {plans.map((plan) => (
                <Grid key={plan.name} size={{ xs: 12, md: 4 }} sx={{ display: 'flex', pt: plan.highlight ? 1.5 : 0 }}>
                  <PricingCard plan={plan} onCta={onLoginClick} />
                </Grid>
              ))}
            </Grid>
            <Box
              sx={{
                textAlign: 'center',
                px: 2,
                py: 2.5,
                borderRadius: 2,
                bgcolor: alpha('#4f46e5', 0.04),
                border: '1px solid',
                borderColor: alpha('#4f46e5', 0.1),
              }}
            >
              <Typography variant="body2" color="text.secondary">
                7-day free trial on <strong>first branch</strong> · Then <strong>₹1,999 every year</strong> per branch
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Mobile waiter app on branch LAN — always free
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* FAQ */}
        <Container maxWidth="md" sx={{ ...sectionSx }} ref={refs.faq}>
          <SectionHeader overline="FAQ" title="Common questions" />
          {faqs.map((item, i) => (
            <Accordion key={item.q} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1.5, borderRadius: '12px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{item.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>

        {/* CTA */}
        <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
          <Card sx={{ background: 'linear-gradient(135deg, #3730a3, #4f46e5)', color: '#fff', textAlign: 'center', p: { xs: 5, md: 8 } }}>
            <Typography variant="h2" sx={{ color: '#fff', mb: 2, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>Ready to run your counter offline?</Typography>
            <Typography sx={{ color: alpha('#fff', 0.88), mb: 4, maxWidth: 520, mx: 'auto' }}>
              Start your 7-day free trial — Main branch included. Connect your first counter PC at no cost.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button size="large" onClick={onLoginClick} sx={{ bgcolor: '#fff', color: 'primary.main', px: 4, '&:hover': { bgcolor: alpha('#fff', 0.9) } }}>Create free account</Button>
              <Button size="large" variant="outlined" onClick={() => scrollTo('pricing')} sx={{ borderColor: alpha('#fff', 0.5), color: '#fff', px: 4, '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.08) } }}>Compare plans</Button>
            </Stack>
          </Card>
        </Container>

        {/* Footer */}
        <Box sx={{ bgcolor: '#0f172a', color: alpha('#fff', 0.85), py: 8 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} sx={{ width: '100%' }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <img src="/logo192.png" alt="HomeBillingPOS Logo" style={{ height: 28, width: 28, borderRadius: 6, backgroundColor: '#ffffff', padding: 2 }} />
                  <Typography variant="h6" fontWeight={800}>Home Billing POS</Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: alpha('#fff', 0.6), maxWidth: 280 }}>
                  Cloud-standard restaurant POS with counter PC sync, branch-based LAN mobile, and free waiter phones on your network.
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Product</Typography>
                <Stack spacing={1}>
                  {NAV.map((n) => (
                    <Button key={n.ref} onClick={() => scrollTo(n.ref)} sx={{ color: alpha('#fff', 0.65), justifyContent: 'flex-start', p: 0, minWidth: 0, '&:hover': { color: '#fff', bgcolor: 'transparent' } }}>{n.label}</Button>
                  ))}
                </Stack>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Support & legal</Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.6) }}>
                    Questions about setup, licensing, or branches? We are here to help.
                  </Typography>
                  <Button
                    href="mailto:support@homebillingpos.com"
                    sx={{ color: '#818cf8', justifyContent: 'flex-start', p: 0, minWidth: 0, fontWeight: 600, '&:hover': { bgcolor: 'transparent', color: '#a5b4fc' } }}
                  >
                    support@homebillingpos.com
                  </Button>
                  <Button
                    href="tel:+919867765397"
                    sx={{ color: '#818cf8', justifyContent: 'flex-start', p: 0, minWidth: 0, fontWeight: 600, '&:hover': { bgcolor: 'transparent', color: '#a5b4fc' } }}
                  >
                    +91 98677 65397
                  </Button>
                  <Button href="/privacy" sx={{ color: alpha('#fff', 0.65), justifyContent: 'flex-start', p: 0, minWidth: 0, '&:hover': { color: '#fff', bgcolor: 'transparent' } }}>
                    Privacy Policy
                  </Button>
                  <Button href="/terms" sx={{ color: alpha('#fff', 0.65), justifyContent: 'flex-start', p: 0, minWidth: 0, '&:hover': { color: '#fff', bgcolor: 'transparent' } }}>
                    Terms & Conditions
                  </Button>
                  <Button href="/refund-policy" sx={{ color: alpha('#fff', 0.65), justifyContent: 'flex-start', p: 0, minWidth: 0, '&:hover': { color: '#fff', bgcolor: 'transparent' } }}>
                    Refund Policy
                  </Button>
                </Stack>
              </Grid>
            </Grid>
            <Divider sx={{ borderColor: alpha('#fff', 0.1), my: 4 }} />
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.45) }}>© 2026 Home Billing POS. All rights reserved.</Typography>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}